import React, { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { profile } from "../mock";
import { useReveal } from "../hooks/useReveal";

const initial = { firstName: "", lastName: "", email: "", message: "" };

// Paste a form endpoint here (e.g. from Formspree) to have submissions delivered
// straight to your inbox. While it is empty the form falls back to opening the
// visitor's mail app with the message pre-written, so nothing is ever lost.
const FORM_ENDPOINT = "";

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const { ref, visible } = useReveal();

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openMailFallback = () => {
    const name = [form.firstName, form.lastName].filter(Boolean).join(" ");
    const body = `${form.message}\n\n\u2014 ${name}\n${form.email}`;
    window.location.href =
      `mailto:${profile.email}` +
      `?subject=${encodeURIComponent(`Project enquiry from ${name}`)}` +
      `&body=${encodeURIComponent(body)}`;
    toast.success("Opening your email app to send the message.");
    setForm(initial);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    if (!FORM_ENDPOINT) {
      openMailFallback();
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`Form endpoint returned ${res.status}`);
      setForm(initial);
      toast.success("Thanks! Your message has been sent.");
    } catch (err) {
      // Never claim success we cannot verify — offer a route that does work.
      toast.error(
        `Sorry, that didn't send. Please email ${profile.email} directly.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-[var(--line)] py-3 text-[16px] outline-none focus:border-[var(--ink)] transition-colors placeholder:text-[var(--muted)]/60";

  return (
    <section id="contact" className="py-24 md:py-32 scroll-mt-20">
      <div className="container-wide">
        <div ref={ref} className={`grid md:grid-cols-12 gap-12 lg:gap-20 reveal ${visible ? "is-visible" : ""}`}>
          <div className="md:col-span-5">
            <span className="section-label">Contact</span>
            <h2 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl mt-4 tracking-[0.015em] leading-[0.98]">
              Let&rsquo;s work together.
            </h2>
            <p className="mt-6 text-[17px] leading-relaxed text-[var(--muted)] max-w-md">
              Interested in working together or hiring me? Fill out the form and I&rsquo;ll be in touch shortly. I can&rsquo;t wait to hear from you.
            </p>
            <a
              href={`mailto:${profile.email}`}
              className="inline-flex items-center gap-2 mt-8 font-serif-display text-[clamp(1.125rem,5vw,1.5rem)] italic break-words link-underline"
            >
              {profile.email}
              <ArrowUpRight size={20} />
            </a>
          </div>

          <form onSubmit={handleSubmit} className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="section-label">First name *</label>
              <input className={field} value={form.firstName} onChange={update("firstName")} placeholder="Jane" />
            </div>
            <div>
              <label className="section-label">Last name</label>
              <input className={field} value={form.lastName} onChange={update("lastName")} placeholder="Doe" />
            </div>
            <div className="sm:col-span-2">
              <label className="section-label">Email *</label>
              <input type="email" className={field} value={form.email} onChange={update("email")} placeholder="jane@company.com" />
            </div>
            <div className="sm:col-span-2">
              <label className="section-label">Message *</label>
              <textarea rows={4} className={`${field} resize-none`} value={form.message} onChange={update("message")} placeholder="Tell me a little about your project or role..." />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={submitting}
                className="group inline-flex items-center gap-2 px-8 py-4 bg-[var(--ink)] text-[#FAFAF7] rounded-full text-[15px] hover:bg-[var(--accent)] transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send message"}
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
